import React from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useAppStore } from "../../../appStore";
import { MULISH } from "../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "./troubleshootingStore/troubleshootingStore";
import { Hint } from "./TroubleshootingUi";
import { fetchNetworkTargets } from "./TroubleshootingUtils/troubleshootingApi";
import {
  cidrContains,
  tunnelRoutedRanges,
} from "./TroubleshootingUtils/siteTunnel";
import {
  filterTargets,
  isIpLiteral,
  customerTargetVerdict,
  NetworkTarget,
  OUTSIDE_TUNNEL_MESSAGE,
  PRIVATE_FROM_DEFAULT_MESSAGE,
  withRecordingServerPorts,
} from "./TroubleshootingUtils/probeTargets";
import { ProbeOrigin } from "./troubleshootingInterfaces";

const SOURCE_LABEL: Record<NetworkTarget["source"], string> = {
  camera: "Camera",
  server: "Site server",
  management_server: "Management server",
  event_server: "Event server",
  tunnel_subnet: "Tunnel subnet",
  tunnel_client: "WireGuard device",
  recording_server: "Recording server",
  relay: "Vendor cloud",
  ipsec_range: "Site-to-Site VPN",
  alarm_receiver: "Alarm receiver",
};

/** A row's kind and label, without repeating a label that already names it. */
const optionCaption = (option: NetworkTarget): string => {
  const kind = SOURCE_LABEL[option.source];
  if (!option.label || option.label === kind) return kind;
  return option.label.startsWith(kind)
    ? option.label
    : `${kind} · ${option.label}`;
};

/**
 * Actuate staff type any address; customers type public ones and pick
 * private ones. `internal_user` comes from admin's user info (the server's
 * own test); until admin sends it, only superusers count, the stricter side.
 */
export const useCanTypeAnyHost = (): boolean => {
  const permissions = useAppStore((s) => s.userPermissions) as
    | { internal_user?: boolean; super_user?: boolean }
    | undefined;
  return Boolean(permissions?.internal_user ?? permissions?.super_user);
};

/**
 * Whether this user may check `host` from `origin`, with the reason when
 * not. Option C (ENG-2055): admin decides private addresses from the
 * Default path (403 target_not_yours); the browser only refuses what its
 * known rows (`local` plus the picker's last `network-targets/` rows)
 * already say the Default path can't reach.
 */
export const useTargetCheck = (
  host: string,
  origin: ProbeOrigin,
  local: NetworkTarget[] = []
): { allowed: boolean; message: string | null } => {
  const canTypeAny = useCanTypeAnyHost();
  const tunnels = useTroubleshootingStore((s) => s.tunnels);
  const remote = useTroubleshootingStore((s) => s.networkTargets);
  // A picked tunnel only carries the ranges it routes, for staff too:
  // admin refuses anything else (400), so say so before sending.
  if (origin.kind === "wireguard") {
    const text = host.trim();
    const picked = tunnels.find((tunnel) => tunnel.id === origin.tunnel_id);
    const subnets = picked ? tunnelRoutedRanges(picked) : [];
    if (
      text &&
      isIpLiteral(text) &&
      subnets.length > 0 &&
      !subnets.some((cidr) => cidrContains(cidr, text))
    )
      return { allowed: false, message: OUTSIDE_TUNNEL_MESSAGE };
  }
  if (canTypeAny) return { allowed: true, message: null };
  const verdict = customerTargetVerdict(
    host,
    origin.kind === "wireguard"
      ? {
          kind: "wireguard",
          subnets: (() => {
            const chosen = tunnels.find(
              (tunnel) => tunnel.id === origin.tunnel_id
            );
            return chosen ? tunnelRoutedRanges(chosen) : [];
          })(),
        }
      : { kind: "nat" },
    [...local, ...remote]
  );
  if (verdict.allowed) return { allowed: true, message: null };
  return {
    allowed: false,
    message:
      verdict.reason === "private_from_default"
        ? PRIVATE_FROM_DEFAULT_MESSAGE
        : OUTSIDE_TUNNEL_MESSAGE,
  };
};

/** Local rows first, then the server's, without repeating an address. */
const mergeTargets = (
  local: NetworkTarget[],
  remote: NetworkTarget[]
): NetworkTarget[] => {
  const seen = new Set(local.map((t) => `${t.source}|${t.address}`));
  return [
    ...local,
    ...remote.filter((t) => !seen.has(`${t.source}|${t.address}`)),
  ];
};

/**
 * Host box with a searchable list of the user's own addresses (cameras,
 * site servers, tunnel subnets). Picking one fills the host and hands its
 * ports to the form. Nothing picks the origin: a customer's private
 * address is flagged until they choose the tunnel that routes it.
 */
export const TargetHostField = ({
  id,
  value,
  onChange,
  onPick,
  placeholder,
  origin,
  localTargets = [],
}: {
  id: string;
  value: string;
  onChange: (host: string) => void;
  onPick?: (target: NetworkTarget) => void;
  placeholder?: string;
  /** Where the check will run from; decides whether a private host is allowed. */
  origin: ProbeOrigin;
  /** The picked site's own addresses, listed first and shown even offline. */
  localTargets?: NetworkTarget[];
}) => {
  const sampleData = useTroubleshootingStore((s) => s.sampleData);
  const [targets, setTargets] = React.useState<NetworkTarget[]>([]);
  const [unavailable, setUnavailable] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const check = useTargetCheck(value, origin, localTargets);
  const setNetworkTargets = useTroubleshootingStore((s) => s.setNetworkTargets);
  const siteDetail = useTroubleshootingStore((s) => s.siteDetail);

  // Search as the user types, debounced; the list is scoped by admin.
  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      fetchNetworkTargets(value, sampleData).then((result) => {
        if (cancelled) return;
        if (result.status === "ok") {
          // Recording servers come without ports (ENG-2063).
          const rows = withRecordingServerPorts(result.data, siteDetail);
          setTargets(rows);
          setNetworkTargets(rows);
          setUnavailable(false);
        } else {
          setUnavailable(true);
        }
      });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open, sampleData, siteDetail]);

  return (
    <Box>
      <Autocomplete
        id={id}
        freeSolo
        size="small"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={mergeTargets(filterTargets(localTargets, value), targets)}
        filterOptions={(options) => options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.address
        }
        inputValue={value}
        onInputChange={(_, next, reason) => {
          if (reason !== "reset") onChange(next);
        }}
        onChange={(_, picked) => {
          if (picked && typeof picked !== "string") {
            // A subnet is a range, not a host: leave the prefix to finish.
            const address =
              picked.source === "tunnel_subnet" ||
              picked.source === "ipsec_range"
                ? picked.address.split("/")[0].replace(/\.0$/, ".")
                : picked.address;
            onChange(address);
            onPick?.(picked);
          }
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            key={`${option.source}-${option.address}-${option.cameraId ?? ""}`}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ ...MULISH, fontSize: 14 }}>
                {option.address}
                {option.ports.length ? ` : ${option.ports.join(", ")}` : ""}
              </Typography>
              <Typography sx={{ ...MULISH, fontSize: 12, color: "#6b7280" }}>
                {optionCaption(option)}
                {option.siteName ? ` · ${option.siteName}` : ""}
              </Typography>
            </Box>
          </li>
        )}
        noOptionsText={
          unavailable && localTargets.length === 0
            ? "Pick a site above to list its addresses, or type a public address."
            : "None of your addresses match. Public addresses can be typed."
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!check.allowed}
            sx={{
              "& .MuiInputBase-input": { ...MULISH, fontSize: 14 },
              backgroundColor: "#fff",
            }}
          />
        )}
      />
      {!check.allowed && check.message && (
        <Box data-testid={`${id}-not-yours`} sx={{ mt: 0.5 }}>
          <Hint sx={{ color: "#B91C1C" }}>{check.message}</Hint>
        </Box>
      )}
    </Box>
  );
};
